import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faSpinner, faImage } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import MarkdownEditor from '@/components/ui/MarkdownEditor.tsx';
import ThumbnailEditModal from '@/components/ui/ThumbnailEditModal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import { getVideoProcessingStatus } from '@/utils/videoProcessing.ts';
import VideoProcessingStatus from '@/components/videos/VideoProcessingStatus.tsx';
import VideoUploadButton from '@/components/admin/academy/VideoUploadButton.tsx';
import type { Video, CourseID, ModuleID } from '@/models';
import type { UpdateVideoRequest } from '@/modelsAPI';

interface EditVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: CourseID;
  moduleId: ModuleID;
  video: Video | null;
  onVideoUpdated: (video: Video) => void;
  moduleNoKnowledge?: boolean;
}

export default function EditVideoModal({
  isOpen,
  onClose,
  courseId,
  moduleId,
  video,
  onVideoUpdated,
  moduleNoKnowledge = false
}: EditVideoModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 0
  });
  const [loading, setLoading] = useState(false);
  const [isThumbnailEditOpen, setIsThumbnailEditOpen] = useState(false);

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title,
        description: video.description,
        order: video.order
      });
    }
  }, [video]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!video) return;

    // Validation
    if (!formData.title.trim()) {
      showErrorToast('Bitte geben Sie einen Titel ein');
      return;
    }


    try {
      setLoading(true);

      const updateData: UpdateVideoRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        order: formData.order,
        autoPublish: video.autoPublish,
        autoPublishSendDiscord: video.autoPublishSendDiscord,
        autoPublishSendMail: video.autoPublishSendMail,
        autoPublishSendTelegram: video.autoPublishSendTelegram
      };
      
      const response = await APIRoutes.updateVideo(video.id, updateData);
      
      onVideoUpdated(response);
      onClose();
    } catch (err) {
      console.error('Error updating video:', err);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Video bearbeiten"
      icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Titel *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="z.B. Einführung in die Candlesticks"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung
          </label>
          <MarkdownEditor
            value={formData.description}
            onChange={(value) => setFormData((prev) => ({ ...prev, description: value ?? '' }))}
            placeholder="Eine detaillierte Beschreibung des Videos... (Markdown unterstützt)"
            disabled={loading}
            height={200}
          />
        </div>

        {/* Thumbnail Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Thumbnail
          </label>
          <div className="flex items-center space-x-4">
            {video?.thumbnail && (
              <div className="w-16 h-16 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <img 
                  src={APIRoutes.getBlobContentUrl(video.thumbnail)} 
                  alt="Video thumbnail" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setIsThumbnailEditOpen(true)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30"
            >
              <FontAwesomeIcon icon={faImage} className="w-4 h-4 mr-2" />
              {video?.thumbnail ? 'Thumbnail ändern' : 'Thumbnail hinzufügen'}
            </button>
          </div>
        </div>

        {/* Video Processing Status */}
        {video && (() => {
          const processingStatus = getVideoProcessingStatus(video);
          
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Video-Verarbeitung
              </label>
              
              {processingStatus.showErrorAlert && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
                  <p className="text-sm text-red-600 dark:text-red-300 whitespace-pre-wrap">
                    <strong>Fehler:</strong> {processingStatus.errorMessage}
                  </p>
                </div>
              )}
              
              {processingStatus.showUploadButton && (
                <div className="mb-3">
                  <VideoUploadButton
                    videoId={video.id}
                    moduleNoKnowledge={moduleNoKnowledge}
                    onVideoUpdated={(updatedVideo) => {
                      onVideoUpdated(updatedVideo);
                    }}
                  />
                </div>
              )}
              
              {processingStatus.showProcessingStates && (
                <div className="mb-3">
                  <VideoProcessingStatus video={video} />
                </div>
              )}
              
              {!processingStatus.showUploadButton && !processingStatus.showProcessingStates && !processingStatus.showErrorAlert && (
                <p className="text-sm text-green-600 dark:text-green-400">
                  ✅ Video wurde erfolgreich verarbeitet und ist bereit zur Veröffentlichung
                </p>
              )}
            </div>
          );
        })()}

        <div>
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reihenfolge
          </label>
          <input
            type="number"
            id="order"
            value={formData.order}
            onChange={(e) => setFormData((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
            disabled={loading}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichere...
              </>
            ) : (
              'Änderungen speichern'
            )}
          </button>
        </div>
      </form>
      
      <ThumbnailEditModal
        isOpen={isThumbnailEditOpen}
        onClose={() => setIsThumbnailEditOpen(false)}
        onThumbnailUpdated={() => {
          // Trigger a refresh of the video data if needed
        }}
        type="video"
        courseId={courseId}
        moduleId={moduleId}
        videoId={video?.id}
        currentThumbnailUrl={video?.thumbnail ? APIRoutes.getBlobContentUrl(video.thumbnail) : undefined}
      />
    </Modal>
  );
}